import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES, USER_ROLES } from '../utils/constants';
import { getOnboardingRouteForRoles } from '../utils/postLoginRedirect';
import { RequireAuth } from '../guards/RequireAuth';
import { RequireRole } from '../guards/RequireRole';
import { RequireMembership } from '../guards/RequireMembership';
import { Spinner } from '../components/ui/Spinner';
import { UserRole } from '../modules/platform/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
  requireOnboarding?: boolean;
}

const membershipRoles = new Set<UserRole>([
  USER_ROLES.SCHOOL_ADMIN,
  USER_ROLES.TEACHER,
  USER_ROLES.STUDENT,
  USER_ROLES.PARENT,
]);

/**
 * A school membership is only required when the caller is actually entering the route through a
 * school-scoped role they hold. Platform-level roles (DEV_TEAM, SUPPORT_AGENT) have no
 * `school_id` on their membership, so on routes shared with a school-scoped role they must not be
 * held to the membership check.
 */
const requiresMembership = (
  requiredRole?: UserRole | UserRole[],
  userRoles?: string[]
): boolean => {
  if (!requiredRole) return false;
  const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  const normalizedUserRoles = (userRoles ?? []).map((role) => String(role).toUpperCase());
  return roles.some(
    (role) => membershipRoles.has(role) && normalizedUserRoles.includes(String(role).toUpperCase())
  );
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requireOnboarding = false,
}) => {
  const { user, refreshUser, hasAnyRole } = useAuth();
  const location = useLocation();
  const [onboardingSynced, setOnboardingSynced] = useState(!requireOnboarding);
  const onboardingSyncGenRef = useRef(0);

  const requiredRoles = requiredRole
    ? Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole]
    : [];
  const userHasRequiredRole =
    !requiredRole || (!!user && hasAnyRole(requiredRoles));

  useEffect(() => {
    if (!requireOnboarding || !userHasRequiredRole) return;
    if (user?.onboarding_complete === true) {
      setOnboardingSynced(true);
      return;
    }
    const gen = ++onboardingSyncGenRef.current;
    setOnboardingSynced(false);
    void (async () => {
      try {
        await refreshUser();
      } finally {
        if (onboardingSyncGenRef.current === gen) {
          setOnboardingSynced(true);
        }
      }
    })();
  }, [requireOnboarding, refreshUser, location.pathname, user?.onboarding_complete, userHasRequiredRole]);

  const content = requiredRole ? (
    <RequireRole requiredRole={requiredRole}>
      {requiresMembership(requiredRole, user?.roles) ? (
        <RequireMembership>{children}</RequireMembership>
      ) : (
        children
      )}
    </RequireRole>
  ) : (
    <>{children}</>
  );

  const onboardingRedirect = () => {
    if (requireOnboarding && userHasRequiredRole && !onboardingSynced) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <Spinner size="lg" />
        </div>
      );
    }
    if (
      requireOnboarding &&
      userHasRequiredRole &&
      user &&
      user.onboarding_complete !== true
    ) {
      const onboardingRoute = getOnboardingRouteForRoles(user.roles);
      if (onboardingRoute) {
        return <Navigate to={onboardingRoute} replace />;
      }
    }
    return content;
  };

  return <RequireAuth>{onboardingRedirect()}</RequireAuth>;
};

